import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { BrandService, Brand } from "@/lib/brand-service";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { CheckCircleIcon, ExternalLinkIcon, MapPinIcon, ShieldCheckIcon } from "lucide-react";
import BreadcrumbNavigation from "@/polymet/components/breadcrumb-navigation";
import ProductCard from "@/polymet/components/product-card";

export default function BrandPage() {
  const { brandId = "", brandSlug = "" } = useParams();
  const [brand, setBrand] = useState<Brand | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [authorizedSellers, setAuthorizedSellers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("products");
  
  useEffect(() => {
    const loadBrandData = async () => {
      try {
        setLoading(true);
        const brandService = new BrandService();
        
        // Get brand by ID or slug
        let brandData: Brand | null = null;
        if (brandId) {
          brandData = await brandService.getBrand(brandId);
        } else if (brandSlug) {
          // Implement getBrandBySlug in BrandService if needed
          // brandData = await brandService.getBrandBySlug(brandSlug);
        }
        
        if (brandData) {
          setBrand(brandData);
          
          // Get products by brand
          const productsData = await brandService.getProductsByBrand(brandData.id);
          setProducts(productsData);
          
          // Get authorized sellers
          const sellersData = await brandService.getAuthorizedSellers(brandData.id);
          setAuthorizedSellers(sellersData);
        }
      } catch (error) {
        console.error("Failed to load brand data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadBrandData();
  }, [brandId, brandSlug]);
  
  // Breadcrumb items
  const breadcrumbItems = [
    { name: "Home", href: "/" },
    { name: "Brands", href: "/brands" },
    { name: brand?.name || "Brand", href: `/brands/${brand?.slug || ""}` }
  ];
  
  if (loading) {
    return <div className="container mx-auto px-4 py-8">Loading...</div>;
  }
  
  if (!brand) {
    return <div className="container mx-auto px-4 py-8">Brand not found</div>;
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumb Navigation */}
      <BreadcrumbNavigation items={breadcrumbItems} />
      
      {/* Brand Header */}
      <div className="mt-6 mb-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Brand Logo */}
          <div className="flex-shrink-0">
            <div className="w-32 h-32 rounded-lg border overflow-hidden bg-white p-2 flex items-center justify-center">
              {brand.logo_url ? (
                <img 
                  src={brand.logo_url} 
                  alt={brand.name} 
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <div className="text-2xl font-bold text-center text-gray-400">
                  {brand.name.charAt(0)}
                </div>
              )}
            </div>
          </div>
          
          {/* Brand Info */}
          <div className="flex-grow">
            <div className="flex items-center mb-2">
              <h1 className="text-3xl font-bold mr-2">{brand.name}</h1>
              {brand.verified && (
                <Badge variant="secondary" className="gap-1">
                  <CheckCircleIcon className="h-3.5 w-3.5" />
                  Verified Brand
                </Badge>
              )}
            </div>
            
            <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
              {brand.country_of_origin && (
                <div className="flex items-center">
                  <MapPinIcon className="h-4 w-4 mr-1" />
                  {brand.country_of_origin}
                </div>
              )}
              {brand.founded_year && (
                <div>Est. {brand.founded_year}</div>
              )}
              {brand.website_url && (
                <a 
                  href={brand.website_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center text-blue-600 hover:underline"
                >
                  Official Website
                  <ExternalLinkIcon className="h-3.5 w-3.5 ml-1" />
                </a>
              )}
            </div>
            
            {brand.description && (
              <p className="text-gray-700">{brand.description}</p>
            )}
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <Tabs defaultValue="products" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="products">
            Products ({products.length})
          </TabsTrigger>
          <TabsTrigger value="authorized-sellers">
            Authorized Sellers ({authorizedSellers.length})
          </TabsTrigger>
          <TabsTrigger value="about">
            About
          </TabsTrigger>
        </TabsList>
        
        {/* Products Tab */}
        <TabsContent value="products" className="space-y-6">
          {products.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  id={product.id}
                  name={product.name}
                  condition={product.condition}
                  price={product.price}
                  previousPrice={product.previous_price}
                  currency={product.currency}
                  image={product.images?.[0] || "https://via.placeholder.com/300x200?text=No+Image"}
                  manufacturer={brand.name}
                  model={product.model}
                  year={product.year}
                  location={product.location}
                  rating={product.rating}
                  reviewCount={product.review_count}
                  featured={product.featured}
                  availableForRent={product.available_for_rent}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 border rounded-md">
              <p className="text-gray-500">No products available from this brand yet.</p>
            </div>
          )}
        </TabsContent>
        
        {/* Authorized Sellers Tab */}
        <TabsContent value="authorized-sellers" className="space-y-6">
          {authorizedSellers.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {authorizedSellers.map((seller) => (
                <Card key={seller.supplier_id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          {seller.suppliers.logo ? (
                            <AvatarImage src={seller.suppliers.logo} alt={seller.suppliers.name || 'Supplier'} />
                          ) : (
                            <AvatarFallback>
                              {(seller.suppliers.name && typeof seller.suppliers.name === 'string') 
                                ? seller.suppliers.name.charAt(0) 
                                : 'S'}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div>
                          <CardTitle className="text-lg">{seller.suppliers.name}</CardTitle>
                          {seller.suppliers.location && (
                            <CardDescription>
                              <MapPinIcon className="h-3.5 w-3.5 inline mr-1" />
                              {seller.suppliers.location}
                            </CardDescription>
                          )}
                        </div>
                      </div>
                      {seller.verified && (
                        <Badge variant="outline" className="gap-1">
                          <ShieldCheckIcon className="h-3.5 w-3.5" />
                          Verified
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {seller.valid_until && (
                      <p className="text-sm text-gray-500">
                        Authorized until: {new Date(seller.valid_until).toLocaleDateString()}
                      </p>
                    )}
                  </CardContent>
                  <CardFooter>
                    <Button variant="outline" className="w-full" asChild>
                      <Link to={`/suppliers/${seller.suppliers.id}`}>
                        View Supplier
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 border rounded-md">
              <p className="text-gray-500">No authorized sellers listed for this brand.</p>
            </div>
          )}
        </TabsContent>
        
        {/* About Tab */}
        <TabsContent value="about" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>About {brand.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {brand.description ? (
                <div>
                  <h3 className="font-medium mb-2">Description</h3>
                  <p>{brand.description}</p>
                </div>
              ) : (
                <p className="text-gray-500">No detailed information available for this brand.</p>
              )}
              
              {brand.founded_year && (
                <div>
                  <h3 className="font-medium mb-2">Founded</h3>
                  <p>{brand.founded_year}</p>
                </div>
              )}
              
              {brand.country_of_origin && (
                <div>
                  <h3 className="font-medium mb-2">Country of Origin</h3>
                  <p>{brand.country_of_origin}</p>
                </div>
              )}
              
              {brand.website_url && (
                <div>
                  <h3 className="font-medium mb-2">Official Website</h3>
                  <a 
                    href={brand.website_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center"
                  >
                    {brand.website_url}
                    <ExternalLinkIcon className="h-3.5 w-3.5 ml-1" />
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
